import React, { useContext, useState } from 'react';
import type { FormApi } from 'final-form';
import { Helmet as MetaTags } from 'react-helmet';
import {
  navigate,
  RouteComponentProps,
  useLocation,
  WindowLocation,
} from '@gatsbyjs/reach-router';
import { Auth } from '~/amplify';
import { AuthContext } from '~/Root/Auth';
import { Body, Heading1 } from '~/components/atom/Typography';
import { Form, Field } from '~/components/organism/Forms';
import OTPInput from '~/components/molecule/OTPInput';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button from '~/components/atom/Button';
import TextButton from '~/components/atom/TextButton';
import AuthFormWrapper from '../components/AuthFormWrapper';
import UnauthNav from '~/components/organism/UnauthNav';
import createPageTitle from '~/util/createPageTitle';
import { reporter } from '~/hooks/useErrorReporter';
import { isExpiredOrNotAuthorizedSessionError } from '~/Errors';
import useIntercom from '~/hooks/useIntercom';
import { AUTH_PATHS } from '~/components/page/Auth/constants';
import styled, { css } from 'styled-components';
import Catalog from '~/Catalog';
import { PROFILE_PATH } from '../../Settings/Profile/constants';
import { SIGN_IN_STEP } from '~/amplify/constants';
import Icon from '~/components/atom/Icon';
import { useGetMyAccountsLazyQuery } from '~/graphql/types';
import { MFA_SETTINGS_BLOCK_ID } from '../../Settings/Profile/components/MFASettingsBlock';
import useUpdateMFAPreferenceAndSyncUser from '~/amplify/utils/updateMFAPreferenceAndSyncUser';

export const text = {
  title: 'Account\u00ADherstel',
  selectionDescription:
    'Je kunt je account herstellen door een code naar je telefoon te sturen (SMS). Hiermee worden je tweestaps\u00ADverificatie-instellingen gereset.',
  sendToPhone: 'Stuur een code naar mijn telefoonnummer (SMS)',
  backToTotp: 'Terug naar authenticator-app',
  needHelp: 'Ik heb hulp nodig.',
  needHelpIntercomMessage:
    'Ik ben mijn Authenticator apparaat kwijt en heb geen backup telefoonnummer.',
  sendingSms: 'SMS-verificatiecode wordt verstuurd…',
  errorMessage:
    'We konden geen code naar je telefoon sturen. Neem contact op met support.',
  smsInstruction:
    'Voer de code in die je hebt ontvangen om je account te herstellen.',
  codeSentTo: (phoneNumber: string) => `Code verzonden naar ${phoneNumber}`,
  submitButton: 'Verifiëren',
  smsErrorMessage: 'Ongeldige code. Probeer het opnieuw.',
  invalidCodeMessage: 'Ongeldige code. Probeer het opnieuw.',
  codeLengthError: 'Voer de volledige 6-cijferige code in.',
};

const SMS_CODE_LENGTH = 6;

const validateSmsCodeForm = ({ code }: { code: string }) => {
  if (!code || code.length !== SMS_CODE_LENGTH) {
    return { code: text.codeLengthError };
  }
  return {};
};

type WindowState = {
  from?: string;
};

type Step = 'selection' | 'loading' | 'code' | 'error';

const MFAAccountRecovery: React.FC<RouteComponentProps> = () => {
  const { ensureLogin } = useContext(AuthContext);
  const location = useLocation() as WindowLocation<WindowState>;
  const intercom = useIntercom();
  const from = location.state?.from;
  const [getMyAccounts] = useGetMyAccountsLazyQuery();
  const { updateMFAPreferenceWithSync } = useUpdateMFAPreferenceAndSyncUser();

  const [step, setStep] = useState<Step>('selection');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [savedPhoneNumber, setSavedPhoneNumber] = useState<string | null>(null);
  const [smsError, setSmsError] = useState<string | null>(null);

  const onSelectSms = async () => {
    setStep('loading');
    setErrorMessage(null);

    try {
      const result = await Auth.confirmSignIn({
        challengeResponse: 'SMS',
      });

      if (result.isSignedIn) {
        // This should be noop, we have not entered the code yet.
        reporter.captureMessage(
          `MFAAccountRecovery: User is signed in but has not entered the code yet.`,
          'warning',
        );
        void navigate('/-/', { replace: true });
        return;
      }

      if (
        result.nextStep.signInStep ===
        SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_SMS_CODE
      ) {
        setSavedPhoneNumber(
          result.nextStep.codeDeliveryDetails?.destination ?? null,
        );
        setStep('code');
        return;
      }

      reporter.captureMessage(
        `MFAAccountRecovery: Unexpected step after SMS selection: ${result.nextStep.signInStep}`,
        'warning',
      );
      setErrorMessage(text.errorMessage);
      setStep('error');
    } catch (err) {
      if (isExpiredOrNotAuthorizedSessionError(err)) {
        void navigate(AUTH_PATHS.LOGIN, {
          replace: true,
          state: { from, message: Catalog.sessionExpired },
        });
        return;
      }

      reporter.captureMessage(
        `MFAAccountRecovery: Failed to switch to SMS challenge: ${err}`,
        'error',
      );

      setErrorMessage(Catalog.genericUnknownErrorMessageShort);
      setStep('error');
    }
  };

  const onSubmitSmsCode = async (
    { code }: { code: string },
    form: FormApi<{ code: string }>,
  ) => {
    setSmsError(null);
    try {
      const signInResult = await Auth.confirmSignIn({
        challengeResponse: code,
      });

      if (signInResult.isSignedIn) {
        ensureLogin();

        // Get an accountId for profile sync, we can use any accountId for the sync
        const result = await getMyAccounts();
        const accountId = result.data?.getMyAccounts?.[0]?.id;

        if (!accountId) {
          // Noop. Navigate anywhere. If they have to set up mfa it will be handled by AuthRoutes/index.tsx as a general fallback
          reporter.captureMessage(
            'MFAAccountRecovery: No accountId found',
            'error',
          );
          void navigate('/-/', { replace: true });
          return;
        }

        // Clear MFA preferences so the user is forced to set MFA up again.
        // On `mfaRequired: true` the Apollo errorHandler will redirect the user
        // to /mfa-setup-totp as soon as the first authenticated request fails.
        // On optional-MFA accounts they will land on the profile page.
        await updateMFAPreferenceWithSync({
          accountId,
          update: { totp: 'DISABLED', sms: 'DISABLED' },
        });

        void navigate(`${PROFILE_PATH}#${MFA_SETTINGS_BLOCK_ID}`, {
          replace: true,
        });
        return;
      }

      reporter.captureMessage(
        `MFAAccountRecovery: SMS verification incomplete: ${signInResult.nextStep.signInStep}`,
        'warning',
      );
      setSmsError(text.smsErrorMessage);
      form.reset({ code: '' });
    } catch (err) {
      if (isExpiredOrNotAuthorizedSessionError(err)) {
        void navigate(AUTH_PATHS.LOGIN, {
          replace: true,
          state: { from, message: Catalog.sessionExpired },
        });
        return;
      }

      reporter.captureMessage(
        `MFAAccountRecovery: Failed to confirmSignIn with SMS: ${err}`,
        'error',
      );

      setSmsError(text.invalidCodeMessage);
      form.reset({ code: '' });
    }
  };

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(text.title)}</title>
      </MetaTags>
      <UnauthNav
        buttons={[
          {
            icon: 'sign-out',
            onClick: () => navigate(AUTH_PATHS.LOGOUT),
            ghost: true,
          },
        ]}
      />
      <AuthFormWrapper>
        <AccountRecoveryHeading
          lineHeight="base"
          margin={[null, null, 'xxxxl']}
          color={{ group: 'accent' }}
          size="xxxl"
          skewed
          wordBreak="break-word"
          width="100%"
          align="center"
        >
          {text.title}
        </AccountRecoveryHeading>

        {step === 'selection' && (
          <JustificationContainer
            direction="column"
            align="stretch"
            justification="center"
            gap="xxl"
            width="100%"
          >
            <Body
              color={{ group: 'white' }}
              size="base"
              margin={[null]}
              wordBreak="break-word"
            >
              {text.selectionDescription}
            </Body>

            <SendSmsButton
              appearance="primary"
              size="medium"
              onClick={onSelectSms}
              label={text.sendToPhone}
              icon="send"
            />

            <SelectionFooter
              align="center"
              justification="space-between"
              gap="l"
              width="100%"
            >
              <TextButton
                appearance="secondary"
                size="medium"
                onClick={() => navigate(AUTH_PATHS.MFA, { state: { from } })}
                label={text.backToTotp}
                icon="arrowLeft"
              />
              <NeedHelpButton
                label={text.needHelp}
                onClick={() => intercom.openChat(text.needHelpIntercomMessage)}
                appearance="accent"
                size="medium"
              />
            </SelectionFooter>
          </JustificationContainer>
        )}

        {step === 'loading' && (
          <JustificationContainer align="center" margin={['xl', null]} gap="s">
            <Icon name="spinner" />
            <Body
              color={{ group: 'white' }}
              size="base"
              margin={[null]}
              wordBreak="break-word"
            >
              {text.sendingSms}
            </Body>
          </JustificationContainer>
        )}

        {step === 'error' && (
          <JustificationContainer
            direction="column"
            gap="xl"
            align="center"
            width="100%"
            margin={['l', null]}
          >
            <Body
              color={{ group: 'danger', variant: 'light' }}
              size="base"
              margin={[null]}
              wordBreak="break-word"
            >
              {errorMessage}
            </Body>

            <NeedHelpButton
              label={text.needHelp}
              onClick={() => intercom.openChat(text.needHelpIntercomMessage)}
              appearance="accent"
              size="medium"
            />
          </JustificationContainer>
        )}

        {step === 'code' && (
          <Form
            initialValues={{ code: '' }}
            onSubmit={onSubmitSmsCode}
            validate={validateSmsCodeForm}
          >
            {({ handleSubmit, submitting, invalid, form }) => (
              <StyledForm onSubmit={handleSubmit}>
                <JustificationContainer
                  align="center"
                  margin={[null, null, 'xl']}
                  direction="column"
                  gap="l"
                  width="100%"
                >
                  <Body
                    color={{ group: 'info', variant: 'light' }}
                    size="base"
                    margin={[null]}
                    fontWeight="medium"
                    wordBreak="break-word"
                  >
                    {text.codeSentTo(savedPhoneNumber ?? '')}
                  </Body>

                  <Body
                    color={{ group: 'white' }}
                    size="base"
                    margin={[null]}
                    wordBreak="break-word"
                  >
                    {text.smsInstruction}
                  </Body>

                  {smsError && (
                    <Body
                      margin={[null]}
                      color={{ group: 'danger', variant: 'light' }}
                      size="base"
                      wordBreak="break-word"
                    >
                      {smsError}
                    </Body>
                  )}

                  <Field name="code">
                    {({ input }) => (
                      <OTPInput
                        length={6}
                        value={input.value ?? ''}
                        onChange={input.onChange}
                        onBlur={input.onBlur}
                        disabled={submitting}
                        autoFocus
                        onComplete={() => {
                          void form.submit();
                        }}
                      />
                    )}
                  </Field>
                </JustificationContainer>

                <JustificationContainer
                  gap="base"
                  width="100%"
                  justification="center"
                >
                  <Button
                    appearance="primary"
                    size="medium"
                    type="submit"
                    disabled={submitting || invalid}
                    loading={submitting}
                    label={text.submitButton}
                  />
                </JustificationContainer>
              </StyledForm>
            )}
          </Form>
        )}
      </AuthFormWrapper>
    </>
  );
};

const StyledForm = styled.form(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    min-width: 0;
    gap: ${theme.space('base')};
  `,
);

/** Skewed headings use max-width: max-content; constrain so text wraps on narrow viewports. */
const AccountRecoveryHeading = styled(Heading1)`
  && {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

/** Button label uses width: max-content; allow wrapping so long Dutch copy fits on small screens. */
const SendSmsButton = styled(Button)(
  ({ theme }) => css`
    align-self: stretch;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;

    & > span:last-of-type {
      white-space: normal;
      width: auto !important;
      max-width: 100%;
      min-width: 0;
      height: auto;
      min-height: 1em;
      line-height: ${theme.lineHeight('base')};
    }
  `,
);

const SelectionFooter = styled(JustificationContainer)(
  ({ theme }) => css`
    min-width: 0;

    ${theme.mq.lessThan('mobile')`
      flex-wrap: wrap-reverse;
      justify-content: center;
      gap: ${theme.space('s')};
    `}
  `,
);

const NeedHelpButton = styled(TextButton)(
  ({ theme }) => css`
    color: ${theme.color('white')};
  `,
);

export default MFAAccountRecovery;
